/* global Office */
import React, { useCallback, useEffect } from "react";
import { ClauseHeader } from "../components/clause-header";
import ChatInputText from "@/components/chat-input-text/chat-input-text";
import { TextareaOnChangeData } from "@fluentui/react-components";
import { getSelectedText } from "@/taskpane/lib";
import { useDocumentClassification } from "@/context/document-classification";
import { rewriteSelectedText } from "@/services/chat-service";
import ReplyCarousel from "../new-clause/reply-carousel/reply-carousel";
import { useRedraftClause } from "@/context/redraft-clause";
import log from "../../../utils/logger";
import { useAbortController } from "@/hooks/use-abort-controller";

export default function RedraftClause() {
  // const styles = useRedraftClauseStyles();
  const { selectedPromptConfig } = useDocumentClassification();
  const { createController, abort, cleanup, getSignal } = useAbortController();
  const {
    query,
    setQuery,
    loadingInsertion,
    setLoadingInsertion,
    loadingRewriting,
    setLoadingRewriting,
    madeRequest,
    setMadeRequest,
    queryReply,
    setQueryReply,
    selectedTextError,
    setSelectedTextError,
  } = useRedraftClause();
  // const [originalText, setOriginalText] = useState<{ text: string; paragraphId: string }>();

  const onTextSelectionChange = useCallback(async () => {
    if (loadingRewriting) return;
    const { text: selectedText } = await getSelectedText();
    setSelectedTextError(selectedText || loadingRewriting ? "" : "Please select some text");
  }, [setSelectedTextError, loadingRewriting]);

  useEffect(() => {
    Office.onReady(() => {
      Office.context.document.addHandlerAsync(
        Office.EventType.DocumentSelectionChanged,
        onTextSelectionChange,
        onTextSelectionChange
      );
    });

    return () => {
      Office.onReady(() => {
        Office.context.document.removeHandlerAsync(Office.EventType.DocumentSelectionChanged, onTextSelectionChange);
      });
    };
  }, [onTextSelectionChange]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      abort();
      setLoadingRewriting(false);
      setLoadingInsertion(false);
      cleanup();
    };
  }, []);

  const onQueryChange = (_ev: React.ChangeEvent<HTMLTextAreaElement>, data: TextareaOnChangeData) => {
    setQuery(data.value);
  };

  const onQueryDocument = async () => {
    try {
      setMadeRequest(true);
      setQueryReply("");
      if (!query) return;
      createController();
      setLoadingRewriting(true);
      const { text: selectedText } = await getSelectedText();
      if (!selectedText) {
        setSelectedTextError("Please select some text.");
        setLoadingRewriting(false);
        return;
      } else {
        setSelectedTextError("");
      }
      const reply = await rewriteSelectedText({
        promptConfigId: selectedPromptConfig.id,
        rewritingDescription: query,
        selectedText,
        abortSignal: getSignal(),
      });
      setQueryReply(reply);
    } catch (e) {
      if (e.message === "Request was cancelled") {
        log.debug("Request was cancelled by user", "onQueryDocument", e);
        setQueryReply("");
      } else {
        log.error("Redraft clause error", "onQueryDocument", e);
      }
    } finally {
      setLoadingRewriting(false);
      setLoadingInsertion(false);
      cleanup();
    }
  };

  const onStopQuery = () => {
    abort();
    setLoadingRewriting(false);
    setLoadingInsertion(false);
    cleanup();
  };

  const loading = loadingRewriting || loadingInsertion;
  return (
    <div>
      <ClauseHeader title="Redraft Clause" />
      <ChatInputText
        placeholder="Describe the changes you want to make"
        query={query}
        onQueryChange={onQueryChange}
        sendBtn={{
          onClick: onQueryDocument,
          disabled: !query || !!selectedTextError || loading,
        }}
        stopBtn={{
          onClick: onStopQuery,
          disabled: !loading,
        }}
        loading={loading}
        error={selectedTextError}
      />
      {madeRequest && (
        <ReplyCarousel
          loadingInsertion={loadingInsertion}
          setLoadingInsertion={setLoadingInsertion}
          loading={loadingRewriting}
          queryReply={queryReply}
        />
      )}
    </div>
  );
}
